import cn from 'classnames'
import InlineDropdown from 'ext-ui/components/InlineDropdown/InlineDropdown'
import TextInput, {
  TextInputRef,
  TextInputVariant,
} from 'ext-ui/components/TextInput/TextInput'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

import style from './AmountInput.style'

const DEFAULT_MIN_VALUE = 0
const DEFAULT_MAX_VALUE = 99_999_999
const DEFAULT_LOCALE = 'en-US'

type AmountInputBaseProps = {
  className?: string
  variant?: TextInputVariant
  label?: string
  requirement?: 'optional' | 'required'
  value: number | undefined
  onChange: (value: number | undefined) => void
  /** ISO-4217 code; shown in the leading slot and drives fraction digits. */
  currency: string
  /** BCP-47 locale for blurred display formatting. */
  locale?: string
  /** Lower bound, enforced on blur so digit-by-digit entry stays typeable. */
  minValue?: number
  /** Upper bound, clamped on every keystroke. */
  maxValue?: number
  errorText?: React.ReactNode
  microcopy?: string
  disabled?: boolean
  /** Already-translated. */
  placeholder?: string
  testId?: string
}

type AmountInputCurrencySelectProps =
  | {
      onCurrencyChange: (currency: string) => void
      supportedCurrencies: string[]
      /** Already-translated accessible name for the currency selector, e.g. "Currency". */
      currencySelectorLabel: string
    }
  | {
      onCurrencyChange?: undefined
      supportedCurrencies?: undefined
      currencySelectorLabel?: undefined
    }

export type AmountInputProps = AmountInputBaseProps &
  AmountInputCurrencySelectProps

const fractionDigitsFor = (locale: string, currency: string): number => {
  try {
    return (
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    )
  } catch {
    return 2
  }
}

const localeSeparators = (
  locale: string,
): { group: string; decimal: string } => {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  }
}

const formatBlurredValue = (
  value: number | undefined,
  locale: string,
  fractionDigits: number,
): string => {
  if (value === undefined) return ''
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)
  } catch {
    return String(value)
  }
}

/**
 * Normalizes a raw input string to a plain `-?\d*\.?\d*` draft, or `null` if
 * nothing numeric survives (e.g. a garbage paste) or the shape is ambiguous
 * (e.g. two decimal points) — callers keep the prior draft in that case so an
 * invalid keystroke/paste can't corrupt what's being edited.
 */
const sanitizeAmountDraft = (
  raw: string,
  locale: string,
  allowNegative: boolean,
): string | null => {
  if (raw === '') return ''
  const { group, decimal } = localeSeparators(locale)
  let normalized = group ? raw.split(group).join('') : raw
  if (decimal && decimal !== '.')
    normalized = normalized.split(decimal).join('.')
  const stripped = normalized.replace(
    allowNegative ? /[^0-9.-]/g : /[^0-9.]/g,
    '',
  )
  if (stripped === '') return null
  const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/
  return pattern.test(stripped) ? stripped : null
}

const truncateDecimals = (value: string, fractionDigits: number): string => {
  const dotIndex = value.indexOf('.')
  if (dotIndex === -1) return value
  return value.slice(0, dotIndex + 1 + fractionDigits)
}

const AmountInput = (props: AmountInputProps): React.ReactElement => {
  const {
    className,
    variant = 'medium',
    label,
    requirement,
    value,
    onChange,
    currency,
    locale = DEFAULT_LOCALE,
    minValue = DEFAULT_MIN_VALUE,
    maxValue = DEFAULT_MAX_VALUE,
    errorText,
    microcopy,
    disabled,
    placeholder,
    testId,
  } = props

  const currencyId = useId()
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')
  const lastEmittedRef = useRef<number | undefined>(value)
  const inputRef = useRef<TextInputRef>(null)

  const fractionDigits = useMemo(
    () => fractionDigitsFor(locale, currency),
    [locale, currency],
  )
  const allowNegative = minValue < 0

  // A parent-driven reset (value changes to something other than what we just
  // emitted) re-seeds the draft while the field is still focused. Keystrokes
  // themselves never take this path — onChange handles those directly.
  useEffect(() => {
    if (focused && value !== lastEmittedRef.current) {
      setDraft(value === undefined ? '' : String(value))
    }
  }, [focused, value])

  const handleFocus = (): void => {
    setFocused(true)
    setDraft(value === undefined ? '' : String(value))
    inputRef.current?.input?.select()
  }

  const handleBlur = (): void => {
    setFocused(false)
    if (value !== undefined && value < minValue) {
      lastEmittedRef.current = minValue
      onChange(minValue)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target = e.target
    const sanitized = sanitizeAmountDraft(target.value, locale, allowNegative)
    if (sanitized === null) {
      // React only touches the DOM when `draft` state actually changes; write
      // the reverted value back directly so a rejected keystroke can't leave
      // the field showing something we never accepted.
      target.value = draft
      return
    }

    const truncated = truncateDecimals(sanitized, fractionDigits)
    if (truncated === '' || truncated === '-' || truncated === '.') {
      target.value = truncated
      setDraft(truncated)
      lastEmittedRef.current = undefined
      onChange(undefined)
      return
    }

    const parsed = parseFloat(truncated)
    const clamped = Math.min(parsed, maxValue)
    const nextDraft = clamped !== parsed ? String(clamped) : truncated
    // Same as above: when truncation/clamping collapses to the same string
    // `draft` already held, a same-value setState bails out without
    // re-rendering, so the DOM write has to happen here too.
    target.value = nextDraft
    setDraft(nextDraft)
    lastEmittedRef.current = clamped
    onChange(clamped)
  }

  const displayValue = focused
    ? draft
    : formatBlurredValue(value, locale, fractionDigits)

  let leadingSlot: React.ReactNode
  if (
    props.onCurrencyChange &&
    props.supportedCurrencies &&
    props.supportedCurrencies.length > 1
  ) {
    const { onCurrencyChange, supportedCurrencies, currencySelectorLabel } =
      props
    leadingSlot = (
      <InlineDropdown
        data-testid={testId ? `${testId}-currency` : 'amount-currency'}
        ariaLabel={currencySelectorLabel}
        options={supportedCurrencies.map((code) => ({
          value: code,
          label: code,
        }))}
        selectedValue={{ value: currency, label: currency }}
        onSelect={(selected): void => onCurrencyChange(String(selected.value))}
        disabled={disabled}
        variant={variant}
      />
    )
  } else {
    // Described-by rather than labelled-by: the currency qualifies the amount
    // the field already has a label for.
    leadingSlot = (
      <span id={currencyId} className="currency-static">
        {currency}
      </span>
    )
  }

  return (
    <div className={cn('AmountInput', className)} css={style}>
      <TextInput
        ref={inputRef}
        variant={variant}
        label={label}
        requirement={requirement}
        errorText={errorText}
        microcopy={microcopy}
        disabled={disabled}
        testId={testId}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        aria-describedby={props.onCurrencyChange ? undefined : currencyId}
        leadingSlot={leadingSlot}
      />
    </div>
  )
}

export default AmountInput
